import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from './firebase';
import { collection, query, where, getDocs, writeBatch, doc, getDoc, serverTimestamp, increment, arrayRemove } from 'firebase/firestore';
import './AdminLedger.css'; 
import './AdminManualPayment.css';

// Helper to calculate the next month chronologically (e.g., "2026-04" -> "2026-05")
const getNextMonth = (YYYY_MM) => {
  let [year, month] = YYYY_MM.split('-').map(Number);
  month++;
  if (month > 12) { month = 1; year++; }
  return `${year}-${String(month).padStart(2, '0')}`;
};

function AdminManualPayment() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Search States
  const [propType, setPropType] = useState('houses');
  const [plotNo, setPlotNo] = useState('');
  
  // Data States
  const [pendingBills, setPendingBills] = useState([]);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [lastBilledMonth, setLastBilledMonth] = useState("");
  const [lastBilledYear, setLastBilledYear] = useState(""); // <-- NEW: Track last billed year
  const [baseMonthlyRate, setBaseMonthlyRate] = useState(0); 
  const [maintYearlyRate, setMaintYearlyRate] = useState(0); 
  const [paidBills, setPaidBills] = useState([]);
  
  // Payment Action States
  const [selectedBillIds, setSelectedBillIds] = useState([]);
  const [arrearsPaymentMode, setArrearsPaymentMode] = useState('WITH_PENALTY'); 
  const [extraAdvanceMonths, setExtraAdvanceMonths] = useState(0);
  const [extraAdvanceYears, setExtraAdvanceYears] = useState(0); // <-- NEW: Advance years
  const [physicalReceiptNo, setPhysicalReceiptNo] = useState('');
  
  // Modal State
  const [showConfirm, setShowConfirm] = useState(false);

  const formatProp = (pt) => pt === 'c_buildings' ? 'Comm. Bldg' : pt === 'n_home' ? 'Nursing Home' : pt;

  // --- 1. FETCH FULL HISTORY & PROPERTY DETAILS ---
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!plotNo.trim()) return alert("Please enter a Plot Number.");
    
    setLoading(true);
    setHasSearched(false);
    setPhysicalReceiptNo('');
    setExtraAdvanceMonths(0);
    setExtraAdvanceYears(0);
    setArrearsPaymentMode('WITH_PENALTY');
    setSelectedBillIds([]);
    setPropertyDetails(null);

    try {
      const q = query(
        collection(db, "bills"),
        where("propertyType", "==", propType),
        where("houseNo", "==", plotNo.trim())
      );
      
      const snap = await getDocs(q);
      const allBills = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Separate unpaid and paid
      const unpaid = allBills.filter(b => b.status === 'UNPAID');
      const paid = allBills.filter(b => b.status === 'PAID' || b.status === 'PAID_IN_ADVANCE');

      // Sort unpaid by month ascending (oldest arrears at the top)
      unpaid.sort((a, b) => a.month.localeCompare(b.month));
      
      // Sort paid by Date of Payment (descending), then by Billing Month (descending)
      paid.sort((a, b) => {
        const timeA = a.paidAt?.seconds || 0;
        const timeB = b.paidAt?.seconds || 0;
        
        if (timeB !== timeA) {
          return timeB - timeA; // Newest payment timestamp first
        }
        
        // If paid in the same transaction/date, newest billing month first
        const monthA = a.month || "";
        const monthB = b.month || "";
        return monthB.localeCompare(monthA);
      });

      setPendingBills(unpaid);
      setPaidBills(paid);
      
      // Determine Last Billed Month & Year
      const today = new Date();
      let lastMonthStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2, '0')}`;
      let lastYearStr = (today.getFullYear() - 1).toString(); // Fallback for yearly

      const standardMonthlyBills = allBills.filter(b => b.billType !== 'YEARLY' && !b.month.includes('YEARLY'));
      const yearlyBills = allBills.filter(b => b.billType === 'YEARLY');

      if (standardMonthlyBills.length > 0) {
        standardMonthlyBills.sort((a, b) => a.month.localeCompare(b.month));
        lastMonthStr = standardMonthlyBills[standardMonthlyBills.length - 1].month;
      }
      
      if (yearlyBills.length > 0) {
        yearlyBills.sort((a, b) => a.year.localeCompare(b.year));
        lastYearStr = yearlyBills[yearlyBills.length - 1].year;
      }

      setLastBilledMonth(lastMonthStr);
      setLastBilledYear(lastYearStr);

      // Fetch Property Details
      const propRef = doc(db, propType, plotNo.trim());
      const propSnap = await getDoc(propRef);
      let pData = {};
      if (propSnap.exists()) {
          pData = propSnap.data();
          setPropertyDetails(pData);
      }

      // Fetch exact base rate from rates_plan dynamically based on size
      const rateRef = doc(db, "rates_plan", propType);
      const rateSnap = await getDoc(rateRef);
      let dynamicBaseRate = 0;
      let dynamicMaintRate = 0;

      if (rateSnap.exists()) {
          const components = rateSnap.data().components || {};
          if (propType === 'houses') {
              const size = String(pData.plotSize || pData.size_yards || '100');
              dynamicBaseRate = (Number(components[size]?.s_w) || 0) + (Number(components[size]?.service) || 0);
              dynamicMaintRate = Number(components[size]?.maintenance) || 0;
          } else if (propType === 'n_home') {
              dynamicBaseRate = (Number(components[plotNo.trim()]?.s_w) || 0) + (Number(components[plotNo.trim()]?.service) || 0);
              dynamicMaintRate = Number(components[plotNo.trim()]?.maintenance) || 0;
          } else {
              dynamicBaseRate = (Number(components.s_w) || 0) + (Number(components.service) || 0);
              dynamicMaintRate = Number(components.maintenance) || 0;
          }
      }
      setBaseMonthlyRate(dynamicBaseRate);
      setMaintYearlyRate(dynamicMaintRate);

      setHasSearched(true);
      setSelectedBillIds(unpaid.map(b => b.id)); // Auto-select all unpaid by default
    } catch (err) {
      alert("Error fetching data: " + err.message);
    }
    setLoading(false);
  };

  const handleToggleBill = (id) => {
    setSelectedBillIds(prev => prev.includes(id) ? prev.filter(bId => bId !== id) : [...prev, id]);
  };

  // --- 2. DYNAMIC MATHEMATICS & WAIVER LOGIC ---
  const selectedBillsData = pendingBills.filter(b => selectedBillIds.includes(b.id));
  
  // Sort chronologically so we waive the oldest 12 first
  const sortedSelectedBills = [...selectedBillsData].sort((a, b) => a.month.localeCompare(b.month));

  let totalBaseArrears = 0;
  let totalPenaltyArrears = 0;
  let waivablePenalty = 0;
  let nonWaivablePenalty = 0; // Yearly penalties go straight here
  let penalizedBillsCount = 0; 
  let waivedBillsTracker = 0; 
  const waivedBillIds = new Set(); 
  
  sortedSelectedBills.forEach(bill => {
    totalBaseArrears += (bill.totalPayable || 0);
    const penAmt = bill.penalty?.amount || 0;
    totalPenaltyArrears += penAmt;
    
    if (penAmt > 0) {
        if (bill.billType === 'YEARLY') {
            nonWaivablePenalty += penAmt; // Yearly is strictly non-waivable
        } else {
            penalizedBillsCount++; 
            if (waivedBillsTracker < 12) {
                waivablePenalty += penAmt;
                waivedBillsTracker++;
                waivedBillIds.add(bill.id); 
            } else {
                nonWaivablePenalty += penAmt; 
            }
        }
    }
  });
  
  const hasArrears = pendingBills.length > 0;
  const isPenaltyWaived = arrearsPaymentMode === 'WAIVE';
  const actualPenalty = isPenaltyWaived ? nonWaivablePenalty : totalPenaltyArrears;
  
  // Calculate Advance Months
  const requiredWaiverMonths = isPenaltyWaived ? waivedBillsTracker : 0;
  const totalAdvanceMonthsToGenerate = requiredWaiverMonths + Number(extraAdvanceMonths || 0);

  const futureMonthsToGenerate = [];
  let currentMonthTracker = lastBilledMonth;
  let advanceBaseTotal = 0;
  const advanceCapsules = []; 

  for(let i=0; i < totalAdvanceMonthsToGenerate; i++) {
    currentMonthTracker = getNextMonth(currentMonthTracker);
    futureMonthsToGenerate.push(currentMonthTracker);
    advanceBaseTotal += baseMonthlyRate;
    advanceCapsules.push({ month: currentMonthTracker, cost: baseMonthlyRate, isMaint: false });
  }

  // Calculate Advance Years (NEW)
  const futureYearsToGenerate = [];
  let currentYearTracker = parseInt(lastBilledYear || new Date().getFullYear() - 1);
  let advanceYearlyTotal = 0;
  const advanceYearCapsules = [];

  for(let i=0; i < extraAdvanceYears; i++) {
    currentYearTracker++;
    futureYearsToGenerate.push(currentYearTracker.toString());
    advanceYearlyTotal += maintYearlyRate;
    advanceYearCapsules.push({ year: currentYearTracker.toString(), cost: maintYearlyRate });
  }

  const grandTotal = totalBaseArrears + actualPenalty + advanceBaseTotal + advanceYearlyTotal;

  // Split pending bills for UI rendering
  const monthlyPending = pendingBills.filter(b => b.billType !== 'YEARLY');
  const yearlyPending = pendingBills.filter(b => b.billType === 'YEARLY');

  // --- 3. PROCESS THE PAYMENT (FIRESTORE BATCH) ---
  const confirmAndPay = async () => {
    if (!physicalReceiptNo.trim()) return alert("Please enter the physical receipt number.");
    if (grandTotal <= 0) return alert("Grand total must be greater than zero.");
    
    setLoading(true);
    try {
      const batch = writeBatch(db);
      const todayIST = new Date().toLocaleString('en-CA', { timeZone: 'Asia/Kolkata' }).split(',')[0];
      const nowIso = new Date().toISOString(); 
      
      const ownerKey = `${propType}_${plotNo}`;
      const defaulterRef = doc(db, 'system_metadata', 'master_defaulters');

      // A. Update Selected Arrears Bills & Metadata
      let appliedWaivers = 0; 

      selectedBillsData.forEach(bill => {
        let waiveThisBill = false;
        if (isPenaltyWaived && (bill.penalty?.amount > 0) && bill.billType !== 'YEARLY') {
            if (appliedWaivers < 12) {
                waiveThisBill = true;
                appliedWaivers++;
            }
        }

        const billRef = doc(db, 'bills', bill.id);
        batch.update(billRef, {
          status: 'PAID',
          paidAt: serverTimestamp(),
          paidByPaymentId: `MANUAL-${physicalReceiptNo}`,
          physicalReceiptNo: physicalReceiptNo,
          clearedByAdmin: auth.currentUser?.uid || 'ADMIN',
          penaltyWaived: waiveThisBill 
        });

        const bType = bill.billType;
        const monthStr = bill.month;
        const basePayable = bill.totalPayable || 0;
        
        // 1. Update Matrix Grid
        const matrixDocId = bType === 'YEARLY' ? `matrix_yearly_${bill.year}` : `matrix_monthly_${monthStr}`;
        const matrixRef = doc(db, 'matrix_metadata', matrixDocId);
        
        batch.set(matrixRef, {
            summary: {
                collectedRevenue: increment(basePayable),
                pendingRevenue: increment(-basePayable),
                paidCount: increment(1),
                unpaidCount: increment(-1)
            },
            grid: {
                [ownerKey]: {
                    status: "PAID",
                    paidAt: nowIso,
                    receiptId: `MANUAL-${physicalReceiptNo}`
                }
            }
        }, { merge: true });
        
        // 2. Reduce Master Defaulters Debt
        batch.set(defaulterRef, {
            [ownerKey]: {
                due: increment(-basePayable),
                monthsPending: arrayRemove(monthStr)
            }
        }, { merge: true });
      });

      // B. Create Advance Monthly Bills & Metadata
      futureMonthsToGenerate.forEach(monthStr => {
        const newBillRef = doc(collection(db, 'bills'));
        batch.set(newBillRef, {
            houseNo: plotNo,
            propertyType: propType,
            month: monthStr,
            billType: 'MONTHLY',
            status: 'PAID_IN_ADVANCE',
            totalPayable: baseMonthlyRate,
            breakup: { manual_advance_base: baseMonthlyRate, maintenance: 0 },
            paidAt: serverTimestamp(),
            paidByPaymentId: `MANUAL-${physicalReceiptNo}`,
            physicalReceiptNo: physicalReceiptNo,
            clearedByAdmin: auth.currentUser?.uid || 'ADMIN'
        });

        const matrixRef = doc(db, 'matrix_metadata', `matrix_monthly_${monthStr}`);
        batch.set(matrixRef, {
            summary: {
                collectedRevenue: increment(baseMonthlyRate),
                paidCount: increment(1)
            },
            grid: {
                [ownerKey]: {
                    status: "ADVANCE",
                    baseDue: baseMonthlyRate,
                    penalty: 0,
                    paidAt: nowIso,
                    receiptId: `MANUAL-${physicalReceiptNo}`,
                    pType: propType,
                    hNo: plotNo
                }
            }
        }, { merge: true });
      });

      // C. Create Advance Yearly Bills & Metadata
      futureYearsToGenerate.forEach(yearStr => {
        const newBillRef = doc(collection(db, 'bills'));
        const monthStr = `${yearStr}-YEARLY`;
        
        batch.set(newBillRef, {
            houseNo: plotNo,
            propertyType: propType,
            month: monthStr,
            year: yearStr,
            billType: 'YEARLY',
            status: 'PAID_IN_ADVANCE',
            totalPayable: maintYearlyRate,
            breakup: { maintenance: maintYearlyRate },
            paidAt: serverTimestamp(),
            paidByPaymentId: `MANUAL-${physicalReceiptNo}`,
            physicalReceiptNo: physicalReceiptNo,
            clearedByAdmin: auth.currentUser?.uid || 'ADMIN'
        });

        const matrixRef = doc(db, 'matrix_metadata', `matrix_yearly_${yearStr}`);
        batch.set(matrixRef, {
            summary: {
                collectedRevenue: increment(maintYearlyRate),
                paidCount: increment(1)
            },
            grid: {
                [ownerKey]: {
                    status: "ADVANCE",
                    baseDue: maintYearlyRate,
                    penalty: 0,
                    paidAt: nowIso,
                    receiptId: `MANUAL-${physicalReceiptNo}`,
                    pType: propType,
                    hNo: plotNo
                }
            }
        }, { merge: true });
      });

      // D. Create Master Ledger Entry
      const ledgerRef = doc(collection(db, 'ledger_entries'));
      let ledgerDesc = `Manual Receipt #${physicalReceiptNo}`;
      
      // --- NEW: CALCULATE EXACT SPLITS ---
      let swArrears = 0, serviceArrears = 0, maintArrears = 0;
      selectedBillsData.forEach(b => {
          swArrears += (b.breakup?.s_w || 0);
          serviceArrears += (b.breakup?.service || 0);
          maintArrears += (b.breakup?.maintenance || 0);
      });

      // Calculate the individual component rates for advance bills dynamically
      let adv_sw_rate = 0, adv_service_rate = 0;
      const lastMonthlyBill = pendingBills.find(b => b.billType === 'MONTHLY');
      if (lastMonthlyBill && lastMonthlyBill.breakup) {
          adv_sw_rate = lastMonthlyBill.breakup.s_w || 0;
          adv_service_rate = lastMonthlyBill.breakup.service || 0;
      } else if (baseMonthlyRate > 0) {
          adv_service_rate = Math.floor(baseMonthlyRate * 0.6); // Fallback estimation
          adv_sw_rate = baseMonthlyRate - adv_service_rate;
      }
      
      let swAdvance = adv_sw_rate * totalAdvanceMonthsToGenerate;
      let serviceAdvance = adv_service_rate * totalAdvanceMonthsToGenerate;
      let maintAdvance = maintYearlyRate * extraAdvanceYears;
      
      let advStrings = [];
      if (totalAdvanceMonthsToGenerate > 0) advStrings.push(`${totalAdvanceMonthsToGenerate} Adv Mth`);
      if (extraAdvanceYears > 0) advStrings.push(`${extraAdvanceYears} Adv Yr`);
      
      if (selectedBillsData.length > 0 && advStrings.length > 0) {
          ledgerDesc += ` (Arrears + ${advStrings.join(' & ')})`;
      } else if (selectedBillsData.length > 0) {
          ledgerDesc += ` (Arrears)`;
      } else if (advStrings.length > 0) {
          ledgerDesc += ` (${advStrings.join(' & ')})`;
      }

      batch.set(ledgerRef, {
        houseNo: plotNo,
        propertyType: propType,
        dateOnly: todayIST,
        type: 'PAYMENT',
        amountSigned: grandTotal * 100,
        referenceId: `MANUAL-${physicalReceiptNo}`,
        description: ledgerDesc,
        timestamp: serverTimestamp(),
        metrics: {
            penaltyCollected: actualPenalty * 100,
            penaltiesWaived: isPenaltyWaived ? (waivablePenalty * 100) : 0,
            swArrears: swArrears * 100,
            serviceArrears: serviceArrears * 100,
            maintArrears: maintArrears * 100,
            swAdvance: swAdvance * 100,
            serviceAdvance: serviceAdvance * 100,
            maintAdvance: maintAdvance * 100,
            advanceCollected: (advanceBaseTotal + advanceYearlyTotal) * 100,
            advanceDiscount: 0,
            baseArrearsCollected: totalBaseArrears * 100,
            advanceMonthsGenerated: futureMonthsToGenerate,
            advanceYearsGenerated: futureYearsToGenerate,
            arrearsMonthsCleared: selectedBillsData.map(b => b.month),
            platformFee: 0 
        }
      });

      await batch.commit();
      
      alert(`✅ Successfully processed ₹${grandTotal.toLocaleString('en-IN')}! Ledger and Matrices updated.`);
      setShowConfirm(false);
      handleSearch({ preventDefault: () => {} });

    } catch (err) {
      alert("Error processing payment: " + err.message);
    }
    setLoading(false);
  };

  return (
    <div className="manual-pay-wrapper">
      <header className="ledger-header">
        <div style={{maxWidth: '1000px', margin: '0 auto', padding: '0 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div>
            <h2 style={{margin: 0, color: '#2c3e50'}}>💵 Manual Payment Desk</h2>
            <p style={{margin: '5px 0 0 0', fontSize: '13px', color: '#7f8c8d'}}>ਮੈਨੂਅਲ ਪੇਮੈਂਟ ਡੈਸਕ</p>
          </div>
          <button onClick={() => navigate('/admin')} style={{padding: '8px 16px', background: '#34495e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'}}>
            ← Back to Admin
          </button>
        </div>
      </header>

      <div style={{maxWidth: '1000px', margin: '0 auto', padding: '0 20px'}}>
        
        {/* SEARCH BAR */}
        <form className="search-section" onSubmit={handleSearch}>
          <div style={{flex: 1}}>
            <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Property Type</label>
            <select className="input-field" style={{marginBottom: 0}} value={propType} onChange={e => setPropType(e.target.value)}>
              <option value="houses">Houses</option><option value="shops">Shops</option><option value="booths">Booths</option><option value="c_buildings">Commercial Bldgs</option><option value="n_home">Nursing Homes</option>
            </select>
          </div>
          <div style={{flex: 1}}>
            <label style={{fontSize:'12px', fontWeight:'bold', color:'#666'}}>Plot / House Number</label>
            <input type="text" className="input-field" style={{marginBottom: 0}} placeholder="e.g. 495" value={plotNo} onChange={e => setPlotNo(e.target.value)} required />
          </div>
          <button type="submit" className="btn-primary-full" style={{width: '200px', marginBottom: 0}}>
            {loading && !hasSearched ? "Searching..." : "🔍 Fetch Account"}
          </button>
        </form>

        {/* RESULTS AREA */}
        {hasSearched && (
          <div className="fade-in-up" style={{paddingBottom: '120px'}}>
            
            {/* PROPERTY DETAILS CARD */}
            {propertyDetails ? (
              <div style={{background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px', marginBottom: '25px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)'}}>
                <div><div style={{fontSize: '12px', color: '#888', fontWeight: 'bold'}}>Owner Name</div><strong style={{color: '#2c3e50', fontSize: '16px'}}>{propertyDetails.ownerName || propertyDetails.name || "N/A"}</strong></div>
                <div><div style={{fontSize: '12px', color: '#888', fontWeight: 'bold'}}>Phone Number</div><strong style={{color: '#2c3e50', fontSize: '16px'}}>{propertyDetails.phone || propertyDetails.mobile || "N/A"}</strong></div>
                <div><div style={{fontSize: '12px', color: '#888', fontWeight: 'bold'}}>Plot Size</div><strong style={{color: '#2c3e50', fontSize: '16px'}}>{propertyDetails.plotSize || propertyDetails.size_yards || "N/A"} {propType === 'houses' ? 'Sq. Yards' : ''}</strong></div>
                <div><div style={{fontSize: '12px', color: '#888', fontWeight: 'bold'}}>Construction Status</div>{propertyDetails.isFilled ? <span style={{background: '#e8f5e9', color: '#2e7d32', padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', display: 'inline-block', marginTop: '4px'}}>✅ Constructed</span> : <span style={{background: '#ffebee', color: '#c0392b', padding: '4px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', display: 'inline-block', marginTop: '4px'}}>🚧 Not Constructed</span>}</div>
                <div style={{gridColumn: 'span 2'}}><div style={{fontSize: '12px', color: '#888', fontWeight: 'bold'}}>Registered Address</div><strong style={{color: '#555', fontSize: '13px'}}>{propertyDetails.address || "N/A"}</strong></div>
              </div>
            ) : (
              <div style={{background: '#fff3e0', color: '#e65100', padding: '15px', borderRadius: '8px', marginBottom: '25px', fontSize: '13px', fontWeight: 'bold', border: '1px solid #ffe0b2'}}>⚠️ Property details not found in the member directory.</div>
            )}

           {/* --- 1. ACCOUNT HISTORY SECTION --- */}
            <h3 style={{color: '#2c3e50', borderBottom: '2px solid #ddd', paddingBottom: '10px', marginBottom: '15px'}}>1. Past Payment History</h3>
            <div style={{marginBottom: '30px'}}>
                {paidBills.length === 0 ? (
                    <p style={{color: '#888', fontStyle: 'italic', textAlign: 'center', padding: '20px'}}>No past payment history found for this property.</p>
                ) : (
                    <div style={{maxHeight: '350px', overflowY: 'auto', paddingRight: '10px'}}>
                        {paidBills.map(bill => {
                            const penAmt = bill.penalty?.amount || 0;
                            const total = (bill.totalPayable || 0) + (bill.penaltyWaived ? 0 : penAmt);
                            const pDate = bill.paidAt?.seconds ? new Date(bill.paidAt.seconds * 1000).toLocaleDateString('en-IN', {day: '2-digit', month: 'short', year: 'numeric'}) : 'Manual Entry';
                            
                            return (
                                <div key={bill.id} style={{background: '#f8fafc', border: '1px solid #e2e8f0', borderLeft: `4px solid ${bill.status === 'PAID_IN_ADVANCE' ? '#3b82f6' : '#22c55e'}`, borderRadius: '8px', padding: '15px', marginBottom: '10px'}}>
                                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                                        <strong style={{fontSize: '16px', color: '#0f172a'}}>
                                            {bill.billType === 'YEARLY' ? `Yearly Maintenance (${bill.year})` : bill.month} 
                                            <span style={{fontSize: '10px', background: '#e2e8f0', color: '#475569', padding: '2px 6px', borderRadius: '4px', marginLeft: '6px', verticalAlign: 'middle'}}>{bill.status.replace(/_/g, ' ')}</span>
                                        </strong>
                                        <strong style={{color: '#2c3e50'}}>₹ {total.toLocaleString('en-IN')}</strong>
                                    </div>
                                    <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#64748b'}}>
                                        <div>
                                            <span>Base: ₹{bill.totalPayable}</span>
                                            {penAmt > 0 && <span style={{marginLeft: '10px', color: bill.penaltyWaived ? '#94a3b8' : '#ef4444', textDecoration: bill.penaltyWaived ? 'line-through' : 'none'}}>Penalty: ₹{penAmt}</span>}
                                        </div>
                                        <div style={{textAlign: 'right'}}>
                                            <span style={{marginRight: '15px'}}>📅 {pDate}</span>
                                            <span style={{fontFamily: 'monospace', background: '#e2e8f0', padding: '2px 6px', borderRadius: '4px'}}>Ref: {bill.paidByPaymentId || bill.physicalReceiptNo || 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            {/* --- 2. CURRENT ARREARS SECTION --- */}
            <h3 style={{color: '#2c3e50', borderBottom: '2px solid #ddd', paddingBottom: '10px', marginBottom: '15px'}}>2. Pending Arrears & Dues</h3>
            {!hasArrears ? (
              <div style={{marginBottom: '30px'}}>
                <div style={{padding: '12px 20px', background: '#e8f5e9', borderRadius: '8px', color: '#2e7d32', marginBottom: '15px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <span>🎉 No Pending Dues!</span>
                </div>
              </div>
            ) : (
              <div style={{marginBottom: '30px'}}>
                
                {/* 2A. MONTHLY DUES BOX */}
                {monthlyPending.length > 0 && (
                    <div style={{marginBottom: '20px'}}>
                        <h4 style={{color: '#34495e', margin: '0 0 10px 0', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px'}}>Monthly Charges</h4>
                        {monthlyPending.map(bill => {
                            const isSelected = selectedBillIds.includes(bill.id);
                            const penAmt = bill.penalty?.amount || 0;
                            const total = (bill.totalPayable || 0) + penAmt;
                            const isWaivedForThisBill = isPenaltyWaived && waivedBillIds.has(bill.id); 

                            return (
                                <label key={bill.id} className={`bill-card ${isSelected ? 'selected' : ''}`} style={{cursor: 'pointer'}}>
                                    <div style={{display: 'flex', gap: '15px', alignItems: 'center'}}>
                                        <input type="checkbox" style={{width: '20px', height: '20px'}} checked={isSelected} onChange={() => handleToggleBill(bill.id)} />
                                        <div>
                                            <strong style={{fontSize: '18px', color: '#333'}}>{bill.month}</strong>
                                            <div className="bill-breakdown" style={{flexWrap: 'wrap'}}>
                                                {bill.breakup?.s_w > 0 && <span>💧 Water/Sewage: <strong>₹{bill.breakup.s_w}</strong></span>}
                                                {bill.breakup?.service > 0 && <span>🧹 Service: <strong>₹{bill.breakup.service}</strong></span>}
                                                {(!bill.breakup) && <span>Base Due: <strong>₹{bill.totalPayable || 0}</strong></span>}
                                                {penAmt > 0 && (
                                                    <span style={{color: isWaivedForThisBill ? '#999' : '#e67e22', textDecoration: isWaivedForThisBill ? 'line-through' : 'none'}}>
                                                        ⚠️ Penalty: <strong>₹{penAmt}</strong>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{textAlign: 'right'}}>
                                        <div style={{fontSize: '12px', color: '#888'}}>Row Total</div>
                                        <strong style={{fontSize: '22px', color: '#2c3e50'}}>₹ {(isWaivedForThisBill ? (bill.totalPayable || 0) : total).toLocaleString('en-IN')}</strong>
                                    </div>
                                </label>
                            )
                        })}
                    </div>
                )}

                {/* 2B. YEARLY MAINTENANCE BOX */}
                {yearlyPending.length > 0 && (
                    <div style={{marginBottom: '20px'}}>
                        <h4 style={{color: '#c0392b', margin: '0 0 10px 0', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '1px'}}>Yearly Maintenance (Vacant Plot)</h4>
                       
                        {yearlyPending.map(bill => {
                            const isSelected = selectedBillIds.includes(bill.id);
                            const penAmt = bill.penalty?.amount || 0;
                            const total = (bill.totalPayable || 0) + penAmt;

                            return (
                                <label key={bill.id} className={`bill-card ${isSelected ? 'selected' : ''}`} style={{cursor: 'pointer', borderLeftColor: '#c0392b'}}>
                                    <div style={{display: 'flex', gap: '15px', alignItems: 'center'}}>
                                        <input type="checkbox" style={{width: '20px', height: '20px'}} checked={isSelected} onChange={() => handleToggleBill(bill.id)} />
                                        <div>
                                            <strong style={{fontSize: '18px', color: '#c0392b'}}>
                                                🚧 Yearly Maintenance ({bill.year})
                                            </strong>
                                            <div className="bill-breakdown" style={{flexWrap: 'wrap'}}>
                                                {bill.breakup?.maintenance > 0 && <span>🏗️ Maintenance: <strong>₹{bill.breakup.maintenance}</strong></span>}
                                                {(!bill.breakup) && <span>Base Due: <strong>₹{bill.totalPayable || 0}</strong></span>}
                                                {penAmt > 0 && (
                                                    <span style={{color: '#e74c3c'}}>
                                                        ⚠️ Non-Waivable Penalty: <strong>₹{penAmt}</strong>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{textAlign: 'right'}}>
                                        <div style={{fontSize: '12px', color: '#888'}}>Row Total</div>
                                        <strong style={{fontSize: '22px', color: '#2c3e50'}}>₹ {total.toLocaleString('en-IN')}</strong>
                                    </div>
                                </label>
                            )
                        })}
                    </div>
                )}

                {/* 2C. Waiver Toggles (Moved below both Monthly and Yearly boxes) */}
                {selectedBillsData.filter(b => b.billType !== 'YEARLY').length > 0 && (
                    <div style={{background: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #e0e0e0', marginTop: '10px'}}>
                        {penalizedBillsCount === 0 ? (
                            <div style={{color: '#27ae60', fontSize: '13px', fontWeight: 'bold'}}>
                                ✅ None of the selected monthly bills have late penalties. No waiver needed!
                            </div>
                        ) : (
                            <div style={{display: 'flex', gap: '20px'}}>
                                <label style={{flex: 1, padding: '15px', border: arrearsPaymentMode === 'WITH_PENALTY' ? '2px solid #e74c3c' : '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', background: arrearsPaymentMode === 'WITH_PENALTY' ? '#fdf2f1' : '#fafafa', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s'}}>
                                    <input type="radio" name="arrearsMode" value="WITH_PENALTY" checked={arrearsPaymentMode === 'WITH_PENALTY'} onChange={e => setArrearsPaymentMode(e.target.value)} />
                                    <div>
                                        <div style={{fontWeight: 'bold', color: arrearsPaymentMode === 'WITH_PENALTY' ? '#c0392b' : '#555'}}>Pay with Penalty</div>
                                        <div style={{fontSize: '12px', color: '#666'}}>Include the late fee.</div>
                                    </div>
                                </label>
                                
                                <label style={{flex: 1, padding: '15px', border: arrearsPaymentMode === 'WAIVE' ? '2px solid #27ae60' : '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', background: arrearsPaymentMode === 'WAIVE' ? '#eefaf2' : '#fafafa', display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s'}}>
                                    <input type="radio" name="arrearsMode" value="WAIVE" checked={arrearsPaymentMode === 'WAIVE'} onChange={e => setArrearsPaymentMode(e.target.value)} />
                                    <div>
                                        <div style={{fontWeight: 'bold', color: arrearsPaymentMode === 'WAIVE' ? '#27ae60' : '#555'}}>
                                            {penalizedBillsCount > 12 ? `Waive 12 Penalties (₹${waivablePenalty})` : `Waive Penalty (₹${waivablePenalty})`}
                                        </div>
                                        <div style={{fontSize: '12px', color: '#666'}}>Adds {waivedBillsTracker} adv month(s). </div>
                                    </div>
                                </label>
                            </div>
                        )}
                    </div>
                )}
              </div>
            )}

            {/* --- 3. ADVANCE PAYMENT SECTION --- */}
            <h3 style={{color: '#2c3e50', borderBottom: '2px solid #ddd', paddingBottom: '10px', marginBottom: '15px'}}>3. Advance Payments</h3>
            
            <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                
                {/* 2A. Monthly Advance Input */}
                <div style={{background: '#f8f9fa', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '20px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '30px', flexWrap: 'wrap'}}>
                        <div>
                            <label style={{fontSize:'12px', fontWeight:'bold', color:'#666', display: 'block', marginBottom: '8px'}}>
                                Advance Months (Standard Charges)
                            </label>
                            <input type="number" min="0" max="24" className="input-field" style={{width: '200px', marginBottom: 0}} value={extraAdvanceMonths} onChange={e => setExtraAdvanceMonths(Number(e.target.value) || 0)} />
                        </div>

                        {totalAdvanceMonthsToGenerate > 0 && (
                            <div style={{background: '#fff', border: '1px solid #cce5ff', padding: '12px 20px', borderRadius: '6px', flex: 1, minWidth: '250px'}}>
                                <div style={{fontSize: '12px', color: '#555', marginBottom: '8px'}}>
                                    <strong>{totalAdvanceMonthsToGenerate}</strong> Advance Month(s) to generate: 
                                    {isPenaltyWaived && waivedBillsTracker > 0 && <span style={{color: '#27ae60', fontSize: '11px', marginLeft: '6px'}}>(Includes {waivedBillsTracker} for waiver)</span>}
                                </div>
                                <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                                    {advanceCapsules.map((cap, idx) => (
                                        <span key={idx} style={{background: '#e8f5e9', border: '1px solid #c8e6c9', color: '#2e7d32', padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold'}}>
                                            {cap.month} (₹{cap.cost})
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* 2B. Yearly Advance Input (Only visible for Vacant Plots) */}
                {propertyDetails && propertyDetails.isFilled === false && (
                    <div style={{background: '#fdf2f1', border: '1px solid #fadbd8', borderRadius: '8px', padding: '20px'}}>
                        <div style={{display: 'flex', alignItems: 'center', gap: '30px', flexWrap: 'wrap'}}>
                            <div>
                                <label style={{fontSize:'12px', fontWeight:'bold', color:'#c0392b', display: 'block', marginBottom: '8px'}}>
                                    Advance Years (Maintenance Charges)
                                </label>
                                <input type="number" min="0" max="5" className="input-field" style={{width: '200px', marginBottom: 0, borderColor: '#fadbd8'}} value={extraAdvanceYears} onChange={e => setExtraAdvanceYears(Number(e.target.value) || 0)} />
                            </div>

                            {extraAdvanceYears > 0 && (
                                <div style={{background: '#fff', border: '1px solid #fadbd8', padding: '12px 20px', borderRadius: '6px', flex: 1, minWidth: '250px'}}>
                                    <div style={{fontSize: '12px', color: '#c0392b', marginBottom: '8px'}}>
                                        <strong>{extraAdvanceYears}</strong> Advance Year(s) to generate: 
                                    </div>
                                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                                        {advanceYearCapsules.map((cap, idx) => (
                                            <span key={`yr-${idx}`} style={{background: '#fff3e0', border: '1px solid #ffe0b2', color: '#e65100', padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold'}}>
                                                Year {cap.year} (₹{cap.cost})
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

          </div>
        )}

        {/* --- 3. CHECKOUT BOTTOM BAR --- */}
        {(selectedBillIds.length > 0 || totalAdvanceMonthsToGenerate > 0 || extraAdvanceYears > 0) && (
          <div className="checkout-bar fade-in-up" style={{ flexWrap: 'wrap', gap: '20px' }}>
            
            {/* Left Side: Metrics */}
            <div style={{display: 'flex', gap: '25px', opacity: 0.9, flexWrap: 'wrap', flex: '1 1 auto'}}>
                {selectedBillIds.length > 0 && (
                    <>
                        <div>
                            <div style={{fontSize: '11px', color: '#a3c2c2', textTransform: 'uppercase'}}>Arrears Base</div>
                            <div style={{fontSize: '16px', fontWeight: 'bold'}}>₹{totalBaseArrears}</div>
                        </div>
                        <div>
                            <div style={{fontSize: '11px', color: '#a3c2c2', textTransform: 'uppercase'}}>Penalty</div>
                            <div style={{fontSize: '16px', fontWeight: 'bold', color: '#e74c3c'}}>
                                ₹{actualPenalty} {isPenaltyWaived && waivablePenalty > 0 && <span style={{fontSize: '10px', textDecoration: 'line-through', color: '#a3c2c2', marginLeft: '4px'}}>₹{waivablePenalty + nonWaivablePenalty}</span>}
                            </div>
                        </div>
                    </>
                )}
                {totalAdvanceMonthsToGenerate > 0 && (
                    <div>
                        <div style={{fontSize: '11px', color: '#a3c2c2', textTransform: 'uppercase'}}>Adv. Months</div>
                        <div style={{fontSize: '16px', fontWeight: 'bold'}}>₹{advanceBaseTotal}</div>
                    </div>
                )}
                {extraAdvanceYears > 0 && (
                    <div>
                        <div style={{fontSize: '11px', color: '#f39c12', textTransform: 'uppercase'}}>Adv. Years</div>
                        <div style={{fontSize: '16px', fontWeight: 'bold', color: '#f39c12'}}>₹{advanceYearlyTotal}</div>
                    </div>
                )}
            </div>

            {/* Right Side: Total and Actions Grouped */}
            <div style={{display: 'flex', alignItems: 'center', gap: '25px', flexWrap: 'wrap'}}>
              <div style={{textAlign: 'right', borderLeft: '1px solid #4a6278', paddingLeft: '25px'}}>
                <div style={{fontSize: '12px', opacity: 0.8, textTransform: 'uppercase', letterSpacing: '1px'}}>Grand Total</div>
                <div style={{fontSize: '32px', fontWeight: '900', color: '#2ecc71'}}>₹ {grandTotal.toLocaleString('en-IN')}</div>
              </div>
              
              <div style={{display: 'flex', gap: '15px', alignItems: 'center'}}>
                <div>
                  <div style={{fontSize: '11px', opacity: 0.8, marginBottom: '4px'}}>Mandatory Receipt No.</div>
                  <input type="text" className="input-field" style={{marginBottom: 0, width: '180px', border: 'none', color: '#333'}} placeholder="Enter Receipt #..." value={physicalReceiptNo} onChange={e => setPhysicalReceiptNo(e.target.value)} />
                </div>
                <button onClick={() => { if(!physicalReceiptNo.trim()) return alert("Receipt number is required!"); setShowConfirm(true); }} className="btn-primary-full" style={{width: 'auto', background: '#27ae60', padding: '12px 25px', marginTop: '16px', whiteSpace: 'nowrap'}}>
                  Complete Payment
                </button>
              </div>
            </div>
            
          </div>
        )}

      </div>

      {/* --- CONFIRMATION MODAL --- */}
      {showConfirm && (
        <div className="modal-overlay" style={{zIndex: 1000}}>
          <div className="modal-box" style={{maxWidth: '500px'}}>
            <div className="modal-header">
              <h3 style={{color: '#27ae60'}}>⚠️ Confirm Manual Entry</h3>
              <button onClick={() => setShowConfirm(false)} className="btn-close">✖</button>
            </div>
            <div className="modal-body" style={{fontSize: '14px', color: '#333'}}>
              
              <div style={{background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #eee', margin: '15px 0'}}>
                
                {selectedBillsData.length > 0 && (
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '8px'}}>
                        <span style={{color: '#666'}}>Arrears Cleared:</span>
                        <strong style={{textAlign: 'right', maxWidth: '60%'}}>{selectedBillsData.map(b => b.billType === 'YEARLY' ? `${b.year}(Y)` : b.month).join(', ')}</strong>
                    </div>
                )}
                
                {totalAdvanceMonthsToGenerate > 0 && (
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '8px'}}>
                        <span style={{color: '#666'}}>Advance Months Generated:</span>
                        <strong style={{color: '#2980b9', textAlign: 'right', maxWidth: '50%'}}>{futureMonthsToGenerate.join(', ')}</strong>
                    </div>
                )}

                {extraAdvanceYears > 0 && (
                    <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '8px'}}>
                        <span style={{color: '#666'}}>Advance Years Generated:</span>
                        <strong style={{color: '#e67e22'}}>{futureYearsToGenerate.join(', ')}</strong>
                    </div>
                )}

                <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px'}}>
                  <span style={{color: '#666'}}>Physical Receipt No:</span>
                  <strong style={{color: '#2c3e50', fontFamily: 'monospace', background: '#e0e0e0', padding: '2px 6px', borderRadius: '4px'}}>{physicalReceiptNo}</strong>
                </div>

                <div style={{marginTop: '15px', paddingTop: '15px', borderTop: '2px dashed #ccc'}}>
                    {selectedBillsData.length > 0 && (
                        <>
                            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', marginBottom: '4px'}}><span>Base Arrears:</span> <span>₹ {totalBaseArrears}</span></div>
                            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', marginBottom: '4px'}}>
                                <span>Late Penalty:</span> 
                                <span style={{color: '#e74c3c'}}>
                                    ₹ {actualPenalty} {isPenaltyWaived && waivablePenalty > 0 && <span style={{textDecoration: 'line-through', color: '#999', marginLeft: '4px'}}>₹{waivablePenalty} (WAIVED)</span>}
                                </span>
                            </div>
                        </>
                    )}
                    
                    {totalAdvanceMonthsToGenerate > 0 && (
                        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', marginBottom: '4px'}}>
                            <span>Advance Months ({totalAdvanceMonthsToGenerate} x ₹{baseMonthlyRate}):</span> 
                            <span>₹ {advanceBaseTotal}</span>
                        </div>
                    )}

                    {extraAdvanceYears > 0 && (
                        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#555', marginBottom: '4px'}}>
                            <span>Advance Years ({extraAdvanceYears} x ₹{maintYearlyRate}):</span> 
                            <span style={{color: '#e67e22'}}>₹ {advanceYearlyTotal}</span>
                        </div>
                    )}
                    
                    <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '20px', fontWeight: '900', color: '#2c3e50'}}>
                        <span>Final Collection:</span>
                        <span style={{color: '#27ae60'}}>₹ {grandTotal.toLocaleString('en-IN')}</span>
                    </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{display: 'flex', gap: '15px'}}>
              <button onClick={() => setShowConfirm(false)} className="btn-primary-full" style={{background: '#e0e0e0', color: '#333'}}>Cancel</button>
              <button onClick={confirmAndPay} disabled={loading} className="btn-primary-full" style={{background: '#27ae60'}}>
                {loading ? "Processing..." : "Confirm & Save to Ledger"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminManualPayment;